import React, { useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import ccmService from '../../services/ccmService';
import Assessments from './Assessments';
import CarePlans from './CarePlans';
import './PHMHub.css';

const TABS = [
  { key: 'overview',     label: 'Overview',      icon: '⊞' },
  { key: 'assessments',  label: 'HRA Assessments', icon: '🩺' },
  { key: 'careplans',    label: 'Care Plans',    icon: '📋' },
  { key: 'timelogs',     label: 'Time Logs',     icon: '⏱' },
];

const PHMHub = () => {
  const { enrollmentId } = useParams();
  const navigate         = useNavigate();
  const [activeTab, setActiveTab]     = useState('overview');
  const [enrollment, setEnrollment]   = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    // Load enrollment / patient summary
    setTimeout(() => {
      setEnrollment({
        id: enrollmentId,
        patient_name: 'Patient',
        mrn: `CCM-${enrollmentId}`,
        status: 'Active',
        care_manager: 'Care Team',
        risk_level: 'High',
        last_contact: new Date().toLocaleDateString(),
        monthly_minutes: 15,
        required_minutes: 20,
      });
      setLoading(false);
    }, 600);
  }, [enrollmentId]);

  const progressPct = enrollment
    ? Math.min(100, Math.round((enrollment.monthly_minutes / enrollment.required_minutes) * 100))
    : 0;

  const riskColors = { Low: '#10b981', Medium: '#f59e0b', High: '#ef4444' };
  const riskColor = enrollment ? (riskColors[enrollment.risk_level] || '#64748b') : '#64748b';

  if (loading) {
    return (
      <div className="phm-loading">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="phm-overview">
            {/* KPI Row */}
            <div className="phm-kpi-row">
              <div className="phm-kpi-card">
                <p className="phm-kpi-label">Risk Level</p>
                <div className="phm-kpi-value" style={{ color: riskColor }}>
                  <span className="phm-kpi-dot" style={{ background: riskColor }} />
                  {enrollment.risk_level}
                </div>
              </div>
              <div className="phm-kpi-card">
                <p className="phm-kpi-label">Last Contact</p>
                <div className="phm-kpi-value">{enrollment.last_contact}</div>
              </div>
              <div className="phm-kpi-card phm-kpi-card--progress">
                <p className="phm-kpi-label">Monthly Time</p>
                <div className="phm-kpi-value">
                  {enrollment.monthly_minutes} <span className="phm-kpi-unit">/ {enrollment.required_minutes} min</span>
                </div>
                <div className="phm-progress-bar">
                  <div
                    className="phm-progress-fill"
                    style={{
                      width: `${progressPct}%`,
                      background: progressPct >= 100 ? '#10b981' : progressPct >= 60 ? '#f59e0b' : '#ef4444'
                    }}
                  />
                </div>
                <p className="phm-progress-pct">{progressPct}% of billing threshold</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="phm-quick-actions">
              <h4 className="phm-section-title">Quick Actions</h4>
              <div className="phm-action-grid">
                <button className="phm-action-card" onClick={() => setActiveTab('assessments')}>
                  <span className="phm-action-icon">🩺</span>
                  <span className="phm-action-label">Start Assessment</span>
                  <span className="phm-action-hint">Document HRA findings</span>
                </button>
                <button className="phm-action-card" onClick={() => setActiveTab('careplans')}>
                  <span className="phm-action-icon">📋</span>
                  <span className="phm-action-label">View Care Plan</span>
                  <span className="phm-action-hint">Goals, objectives & interventions</span>
                </button>
                <button className="phm-action-card" onClick={() => setActiveTab('timelogs')}>
                  <span className="phm-action-icon">⏱</span>
                  <span className="phm-action-label">Log Time</span>
                  <span className="phm-action-hint">Track care management minutes</span>
                </button>
              </div>
            </div>
          </div>
        );

      case 'assessments':
        return <Assessments enrollmentId={enrollmentId} />;

      case 'careplans':
        return (
          <div className="phm-embedded-careplans">
            <CarePlans />
          </div>
        );

      case 'timelogs':
        return (
          <div className="phm-placeholder">
            <span className="phm-placeholder-icon">⏱</span>
            <h4>Time Tracking</h4>
            <p>Use the Time Logs module to record care management minutes for this patient.</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="phm-root">
      {/* Patient Header */}
      <div className="phm-patient-header">
        <div className="phm-patient-avatar">
          {(enrollment.patient_name || 'P').split(' ').map(n => n[0]).join('').slice(0, 2)}
        </div>
        <div className="phm-patient-info">
          <h2 className="phm-patient-name">
            {enrollment.patient_name}
            <span className="phm-ccm-badge">CCM Active</span>
          </h2>
          <div className="phm-patient-meta">
            <span>🪪 {enrollment.mrn}</span>
            <span>👤 Care Manager: {enrollment.care_manager}</span>
          </div>
        </div>
        <div className="phm-header-actions">
          <button
            className="phm-btn-outline"
            onClick={() => navigate(`/ccm/care-plans/${enrollmentId}`)}
          >
            ⏱ Timer
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="phm-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`phm-tab ${activeTab === tab.key ? 'phm-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="phm-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="phm-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default PHMHub;
